import { i18n } from "@lingui/core";
import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import { GUEST_RESUME_LIMIT, RESUME_LIMITS, TIER_PERMISSIONS, UserTier } from "@stylingcv/config";

/**
 * Get the maximum number of resumes allowed for a user tier
 */
export const getMaxResumes = (userTier: UserTier): number => {
  return TIER_PERMISSIONS[userTier].maxResumes || GUEST_RESUME_LIMIT;
};

/**
 * Get the maximum number of resumes for a subscription tier (for server-side)
 */
export const getMaxResumesBySubscriptionTier = (subscriptionTier: string): number => {
  switch (subscriptionTier) {
    case "FREE": {
      return RESUME_LIMITS.FREE;
    }
    case "BASIC": {
      return RESUME_LIMITS.BASIC;
    }
    case "PROFESSIONAL": {
      return RESUME_LIMITS.PROFESSIONAL;
    }
    case "ENTERPRISE": {
      return RESUME_LIMITS.ENTERPRISE;
    }
    default: {
      return GUEST_RESUME_LIMIT;
    }
  }
};

/**
 * Check if a user can create more resumes
 */
export const canCreateMoreResumes = (
  userTier: UserTier,
  currentResumeCount: number,
): { canCreate: boolean; maxAllowed: number; remaining: number } => {
  const maxAllowed = getMaxResumes(userTier);
  const remaining = Math.max(0, maxAllowed - currentResumeCount);

  return {
    canCreate: currentResumeCount < maxAllowed,
    maxAllowed,
    remaining,
  };
};

/**
 * Check if a guest user can create more resumes
 */
export const canGuestCreateResume = (currentResumeCount: number): boolean => {
  return currentResumeCount < GUEST_RESUME_LIMIT;
};

/**
 * Get the user tier from subscription tier
 */
export const getUserTierFromSubscription = (subscriptionTier: string): UserTier => {
  switch (subscriptionTier) {
    case "FREE": {
      return UserTier.FREE;
    }
    case "BASIC": {
      return UserTier.PREMIUM_BASIC;
    }
    case "PROFESSIONAL": {
      return UserTier.PAID_TOKENS;
    }
    case "ENTERPRISE": {
      return UserTier.PREMIUM_ENTERPRISE;
    }
    default: {
      return UserTier.GUEST;
    }
  }
};

/**
 * Internal config data with message descriptors
 */
const RESUME_LIMIT_DATA = {
  GUEST: {
    limit: GUEST_RESUME_LIMIT,
    description: "Guest users can create 1 resume",
  },
  FREE: {
    limit: RESUME_LIMITS.FREE,
    description: "Free users can create 2 resumes",
  },
  BASIC: {
    limit: RESUME_LIMITS.BASIC,
    description: "Basic subscribers can create 5 resumes",
  },
  PROFESSIONAL: {
    limit: RESUME_LIMITS.PROFESSIONAL,
    description: "Professional subscribers can create 10 resumes",
  },
  ENTERPRISE: {
    limit: RESUME_LIMITS.ENTERPRISE,
    description: msg`Enterprise subscribers have unlimited resumes`,
  },
} as const;

/**
 * Resume limit configuration for easy reference
 * Use getResumeLimitConfig() for translated descriptions
 */
export const RESUME_LIMIT_CONFIG = RESUME_LIMIT_DATA;

/**
 * Get resume limit config with translated descriptions
 */
export const getResumeLimitConfig = () => ({
  GUEST: {
    limit: RESUME_LIMIT_DATA.GUEST.limit,
    description: RESUME_LIMIT_DATA.GUEST.description,
  },
  FREE: {
    limit: RESUME_LIMIT_DATA.FREE.limit,
    description: RESUME_LIMIT_DATA.FREE.description,
  },
  BASIC: {
    limit: RESUME_LIMIT_DATA.BASIC.limit,
    description: RESUME_LIMIT_DATA.BASIC.description,
  },
  PROFESSIONAL: {
    limit: RESUME_LIMIT_DATA.PROFESSIONAL.limit,
    description: RESUME_LIMIT_DATA.PROFESSIONAL.description,
  },
  ENTERPRISE: {
    limit: RESUME_LIMIT_DATA.ENTERPRISE.limit,
    description: i18n._(RESUME_LIMIT_DATA.ENTERPRISE.description as MessageDescriptor),
  },
});
